import { FC } from 'react'
import { ListItem, Tag } from '@lmc/ui-kit'
import { Carer, Home, IncidentReportItem, Resident } from '@lmc/ts-common'
import { tagColorMap } from 'screens/Logs/LogScreen/components/IncidentLogScreens/utils'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import _ from 'lodash'
import { AttachmentsRow } from 'screens/Logs/LogScreen/components'
import { User } from '@sentry/react'
import { Link } from 'react-router-dom'

type Props = {
  incident: IncidentReportItem
  homes?: Home[]
  hasOnClick?: boolean
  testId?: string
  index: number
}

const DATE_FORMAT = 'DD/MM/YYYY HH:mm'
const getName = (doc: Resident | User): string =>
  `${doc?.name?.first} ${doc?.name?.last}`

export const getEditedByContent = (
  incident: IncidentReportItem,
  editedBy?: User
) => {
  if (editedBy) {
    return `Edited by ${getName(editedBy)} at ${dayjs(
      incident?.log?.editedAt
    ).format('DD/MM/YYYY HH:mm')}`
  }
  return ''
}

export const getItemDetailContent = (
  incident: IncidentReportItem,
  resident?: Resident,
  home?: Home,
  witnessedBy?: Carer
) => {
  if (home) {
    return `${home?.name} • ${dayjs(incident?.log?.timeLogged).format(
      DATE_FORMAT
    )} • Witnessed by ${witnessedBy ? getName(witnessedBy) : 'N/A'}`
  }

  return `${resident?.name?.first} ${resident?.name?.last} • ${dayjs(
    incident?.log?.timeLogged
  ).format(DATE_FORMAT)}${
    witnessedBy ? ` • Witnessed by ${getName(witnessedBy)}` : ''
  }`
}

export const IncidentListItem: FC<Props> = ({
  incident,
  homes,
  hasOnClick = true,
  testId = 'incident-list-item',
  index
}) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const items = useSelector(
    (state: { data: { [`log-category-items`]: any } }) =>
      state.data['log-category-items']
  )

  const residents = useSelector(
    (state: { data: { residents: Resident[] } }) => state.data.residents
  )

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const getResidentById = (id?: string) => _.find(residents, { id })
  const getHomeById = (id?: string) => _.find(homes, { id })
  const getCarerById = (id?: string) => _.find(carers, { id })

  const icon = items?.find(
    (item: any) => item.category === incident.log.category
  ).icon

  const hasAttachments = !!incident?.attachments?.length

  const isDeleted = incident?.isDeleted

  // eslint-disable-next-line no-underscore-dangle
  const logId = (incident?.log as unknown as { _id: string })._id.toString()

  const listItemComponent = (
    <ListItem
      data-testid={`${testId}-${index}`}
      titleChildren={
        <StyledText level={2} data-testid={`${testId}-${index}-details`}>
          {getItemDetailContent(
            incident,
            getResidentById(incident?.serviceUser as unknown as string),
            getHomeById((incident?.log as unknown as { home: string })?.home),
            getCarerById(incident?.log?.witnessedBy as unknown as string)
          )}
        </StyledText>
      }
      cursor={!hasOnClick ? 'default' : 'pointer'}
      hasBorder={false}
      iconBackgroundColor="white"
      titleContent={
        <StyledHeadingTitle
          className="title"
          isDeleted={isDeleted}
          data-testid={`${testId}-${index}-title`}
        >
          {incident.log.title}
        </StyledHeadingTitle>
      }
      subTitleContent={
        <StyledTextTitle
          isDeleted={isDeleted}
          data-testid={`${testId}-${index}-description`}
        >
          {incident.log.description}
        </StyledTextTitle>
      }
      iconContent={
        <CategoryImageContainer
          color={isDeleted ? '#999999' : '#FF3366'}
          data-testid={`${testId}-${index}-icon-container`}
        >
          <CategoryImage
            src={icon}
            alt="category"
            data-testid={`${testId}-${index}-icon`}
          />
        </CategoryImageContainer>
      }
      bottomChildren={
        <MainContentWrapper data-testid={`${testId}-${index}-bottom-content`}>
          <StyledText level={2} data-testid={`${testId}-${index}-edited-by`}>
            {getEditedByContent(
              incident,
              getCarerById(incident?.log?.editedBy as unknown as string)
            )}
          </StyledText>
          {hasAttachments ? (
            <AttachmentsRow
              attachments={incident.attachments}
              data-testid={`${testId}-${index}-attachments`}
            />
          ) : undefined}
        </MainContentWrapper>
      }
      alignChildrenTop
    >
      <Tag
        color={tagColorMap[incident.status]}
        data-testid={`${testId}-${index}-status-tag`}
      >
        {t(incidentReportsConstants[incident.status])}
      </Tag>
    </ListItem>
  )

  if (!hasOnClick) {
    return listItemComponent
  }

  return (
    <Link
      to={`/reports/incidents/list/${logId}`}
      data-testid={`${testId}-${index}-link`}
    >
      {listItemComponent}
    </Link>
  )
}

const CategoryImageContainer = styled.div<{ color?: string | undefined }>`
  background-color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: 'hidden';
  width: 40px;
  height: 40px;
  margin-bottom: auto;
  margin-top: 8px;
`
const CategoryImage = styled.img`
  width: 15px;
  height: 15px;
`

const MainContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const StyledTextTitle = styled(Text)<{ isDeleted: boolean }>`
  white-space: pre-wrap;
  color: ${(props) =>
    props.isDeleted
      ? props.theme.color.neutral.color5
      : props.theme.color.neutral.color3};
`

const StyledHeadingTitle = styled(Text)<{ isDeleted: boolean }>`
  color: ${(props) =>
    props.isDeleted
      ? props.theme.color.neutral.color5
      : props.theme.color.neutral.color3};
  font-size: 16px;
  font-weight: bold;
`
